// ============================================================================
// AnalysisVisualizations — extracts structured data from rawAnalysisText and
// renders 3 high-impact viral $1B widgets above the raw markdown:
//   1) Probability scenarios as horizontal progress bars
//   2) Execution decision block as colored Entry/SL/TP cards
//   3) Mini price journey from At Decision -> Current (SVG sparkline)
// All parsing is regex-based on the AI output conventions:
//   '- **Bullish Scenario**: ... Probability: 35%'
//   'Decision: TRADE / NO_TRADE / WAIT'
//   'Entry_Price: $X' or '- **Entry Price**: $X'
//   'Stop_Loss: $X' / 'Take_Profit_1: $X' etc.
// ============================================================================
import React from 'react';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiTrendingDown, FiMinus, FiTarget, FiShield, FiZap } = FiIcons;

// ─────────────────────────────────────────────────────────────────────────────
// PARSERS

const numFromMatch = (m) => {
  if (!m) return null;
  const cleaned = String(m[1] || m[0] || '').replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
};

function parseProbabilities(text) {
  if (!text) return null;
  const out = { bullish: null, bearish: null, neutral: null };
  // Match patterns like 'Bullish ... 35%' or 'Bullish Scenario: 35%' (case-insensitive)
  const bullM = text.match(/bullish[^\n%]*?(\d{1,3})\s*%/i);
  const bearM = text.match(/bearish[^\n%]*?(\d{1,3})\s*%/i);
  const neutM = text.match(/neutral[^\n%]*?(\d{1,3})\s*%/i);
  if (bullM) out.bullish = parseInt(bullM[1], 10);
  if (bearM) out.bearish = parseInt(bearM[1], 10);
  if (neutM) out.neutral = parseInt(neutM[1], 10);
  // Require at least 2 of 3 to render meaningfully
  const found = [out.bullish, out.bearish, out.neutral].filter((x) => x !== null).length;
  return found >= 2 ? out : null;
}

function parseExecutionBlock(text, aiDecision) {
  if (!text && !aiDecision) return null;
  const block = {
    entry: aiDecision?.entryPrice ?? null,
    stopLoss: aiDecision?.stopLoss ?? null,
    tp1: aiDecision?.takeProfit1 ?? null,
    tp2: aiDecision?.takeProfit2 ?? null,
    tp3: aiDecision?.takeProfit3 ?? null,
    riskReward: aiDecision?.riskReward ?? null,
  };
  if (text) {
    // Fallback regex parsers if aiDecision didn't have values
    if (block.entry === null) {
      const m = text.match(/(?:entry[_\s]*price|entry)\s*[:=]\s*\$?\s*([\d,.]+)/i);
      block.entry = numFromMatch(m);
    }
    if (block.stopLoss === null) {
      const m = text.match(/(?:stop[_\s]*loss|sl)\s*[:=]\s*\$?\s*([\d,.]+)/i);
      block.stopLoss = numFromMatch(m);
    }
    if (block.tp1 === null) {
      const m = text.match(/(?:take[_\s]*profit[_\s]*1|tp\s*1|tp1)\s*[:=]\s*\$?\s*([\d,.]+)/i);
      block.tp1 = numFromMatch(m);
    }
    if (block.tp2 === null) {
      const m = text.match(/(?:take[_\s]*profit[_\s]*2|tp\s*2|tp2)\s*[:=]\s*\$?\s*([\d,.]+)/i);
      block.tp2 = numFromMatch(m);
    }
    if (block.tp3 === null) {
      const m = text.match(/(?:take[_\s]*profit[_\s]*3|tp\s*3|tp3)\s*[:=]\s*\$?\s*([\d,.]+)/i);
      block.tp3 = numFromMatch(m);
    }
    if (block.riskReward === null) {
      const m = text.match(/(?:risk[_\s]*reward|r\s*:?\s*r)\s*[:=]\s*([\d.]+)/i);
      block.riskReward = numFromMatch(m);
    }
  }
  // Need at least entry + (stop OR tp1) for the block to be useful
  if (block.entry === null || (block.stopLoss === null && block.tp1 === null)) return null;
  return block;
}

const formatPrice = (n) => {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ─────────────────────────────────────────────────────────────────────────────
// 1. PROBABILITY BARS

function ProbabilityBars({ probs }) {
  const total = (probs.bullish || 0) + (probs.bearish || 0) + (probs.neutral || 0);
  const items = [
    { key: 'bullish', label: 'BULLISH', value: probs.bullish, color: '#10b981', icon: FiTrendingUp },
    { key: 'bearish', label: 'BEARISH', value: probs.bearish, color: '#ef4444', icon: FiTrendingDown },
    { key: 'neutral', label: 'NEUTRAL', value: probs.neutral, color: '#94a3b8', icon: FiMinus },
  ].filter((it) => it.value !== null);

  return (
    <div className="rounded-xl border border-white/10 bg-gray-900/60 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ ai scenarios</span>
          <h4 className="text-sm font-bold text-white">Probability distribution</h4>
        </div>
        {total > 0 && (
          <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-500">
            sum {total}%
          </span>
        )}
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.key}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <SafeIcon icon={it.icon} className="text-xs" style={{ color: it.color }} />
                <span className="text-xs font-mono uppercase tracking-[0.15em]" style={{ color: it.color }}>
                  {it.label}
                </span>
              </div>
              <span className="text-sm font-mono font-bold tabular-nums" style={{ color: it.color }}>
                {it.value}%
              </span>
            </div>
            <div className="relative h-2 rounded-full overflow-hidden bg-white/[0.04]">
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
                style={{
                  width: `${Math.min(100, it.value)}%`,
                  background: `linear-gradient(90deg, ${it.color}80 0%, ${it.color} 100%)`,
                  boxShadow: `0 0 12px ${it.color}66`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. EXECUTION DECISION BLOCK CARDS

function ExecutionCards({ block, direction }) {
  const isLong = direction === 'LONG';
  const tpColor = '#10b981';
  const slColor = '#ef4444';
  const entryColor = '#22d3ee';

  const cards = [
    { key: 'entry', label: 'Entry', value: block.entry, color: entryColor, icon: FiTarget },
    { key: 'sl', label: 'Stop Loss', value: block.stopLoss, color: slColor, icon: FiShield },
    { key: 'tp1', label: 'TP 1', value: block.tp1, color: tpColor, icon: FiZap },
    { key: 'tp2', label: 'TP 2', value: block.tp2, color: tpColor, icon: FiZap },
    { key: 'tp3', label: 'TP 3', value: block.tp3, color: tpColor, icon: FiZap },
  ].filter((c) => c.value !== null);

  return (
    <div className="rounded-xl border border-white/10 bg-gray-900/60 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ execution plan</span>
          <h4 className="text-sm font-bold text-white">Entry / Stops / Targets</h4>
        </div>
        {block.riskReward !== null && (
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-gray-500">r:r</div>
            <div className="text-sm font-mono font-bold text-emerald-300 tabular-nums">
              1 : {Number(block.riskReward).toFixed(2)}
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {cards.map((c) => (
          <div
            key={c.key}
            className="relative rounded-lg border p-3"
            style={{
              borderColor: `${c.color}33`,
              background: `linear-gradient(180deg, ${c.color}0F 0%, transparent 100%)`,
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] font-mono uppercase tracking-[0.2em]" style={{ color: c.color }}>
                {c.label}
              </span>
              <SafeIcon icon={c.icon} className="text-[10px]" style={{ color: `${c.color}99` }} />
            </div>
            <div className="text-base font-mono font-bold tabular-nums text-white">
              ${formatPrice(c.value)}
            </div>
          </div>
        ))}
      </div>
      {direction && direction !== 'NONE' && (
        <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.15em] text-gray-500">
          direction · <span className={isLong ? 'text-emerald-300' : 'text-red-300'}>{direction}</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. PRICE JOURNEY SPARKLINE

function PriceJourney({ atDecision, current, direction }) {
  if (atDecision === null || atDecision === undefined || current === null || current === undefined) return null;
  const delta = current - atDecision;
  const pct = atDecision !== 0 ? (delta / atDecision) * 100 : 0;
  const isUp = delta >= 0;
  // Synthesize an intermediate point for visual interest (random walk would be confusing — use a midpoint)
  const mid = (atDecision + current) / 2;
  const points = [atDecision, mid + (current - atDecision) * 0.15, current];
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const W = 320;
  const H = 56;
  const PAD = 4;
  const toXY = (v, i) => {
    const x = PAD + (i / (points.length - 1)) * (W - 2 * PAD);
    const y = H - PAD - ((v - min) / range) * (H - 2 * PAD);
    return [x, y];
  };
  const coords = points.map(toXY);
  const path = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const lineColor = isUp ? '#10b981' : '#ef4444';
  const isFavorableForLong  = direction === 'LONG'  && isUp;
  const isFavorableForShort = direction === 'SHORT' && !isUp;
  const favorable = isFavorableForLong || isFavorableForShort;

  return (
    <div className="rounded-xl border border-white/10 bg-gray-900/60 p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ price journey</span>
          <h4 className="text-sm font-bold text-white">At decision → now</h4>
        </div>
        <div className="text-right">
          <div className="text-sm font-mono font-bold tabular-nums" style={{ color: lineColor }}>
            {isUp ? '+' : ''}{delta.toFixed(2)}
          </div>
          <div className="text-[10px] font-mono text-gray-500">
            {isUp ? '+' : ''}{pct.toFixed(2)}%
          </div>
        </div>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14 overflow-visible">
          {/* Subtle area fill */}
          <defs>
            <linearGradient id="pj-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={lineColor} stopOpacity="0.3" />
              <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`${path} L${coords[coords.length - 1][0]},${H - PAD} L${coords[0][0]},${H - PAD} Z`}
            fill="url(#pj-fill)"
          />
          <path d={path} fill="none" stroke={lineColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {/* Endpoint dots */}
          <circle cx={coords[0][0]} cy={coords[0][1]} r="3" fill="#22d3ee" stroke="#0a0e1a" strokeWidth="1.5" />
          <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="4" fill={lineColor} stroke="#0a0e1a" strokeWidth="1.5" />
        </svg>
        <div className="flex justify-between mt-2 text-[10px] font-mono">
          <div>
            <div className="text-gray-500 uppercase tracking-wider">at decision</div>
            <div className="text-cyan-300 tabular-nums">${formatPrice(atDecision)}</div>
          </div>
          <div className="text-right">
            <div className="text-gray-500 uppercase tracking-wider">current</div>
            <div className="tabular-nums" style={{ color: lineColor }}>${formatPrice(current)}</div>
          </div>
        </div>
      </div>
      {direction && direction !== 'NONE' && (
        <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.15em]">
          <span className="text-gray-500">verdict ·</span>{' '}
          {favorable ? (
            <span className="text-amber-300">opportunity missed for {direction}</span>
          ) : (
            <span className="text-emerald-300">capital preserved · price moved against {direction}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMBINED ENTRY

export default function AnalysisVisualizations({ analysisText, aiDecision, priceAtDecision, currentPrice }) {
  const probs = parseProbabilities(analysisText);
  const block = parseExecutionBlock(analysisText, aiDecision);
  const hasJourney = priceAtDecision != null && currentPrice != null;

  if (!probs && !block && !hasJourney) return null;

  return (
    <div className="space-y-3">
      {probs && <ProbabilityBars probs={probs} />}
      {block && <ExecutionCards block={block} direction={aiDecision?.direction} />}
      {hasJourney && (
        <PriceJourney atDecision={priceAtDecision} current={currentPrice} direction={aiDecision?.direction} />
      )}
    </div>
  );
}
